import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useState, useEffect } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import AdminRoute from "@/components/AdminRoute";
import { supabase } from "@/integrations/supabase/client";
import { SecurityProtection, RouteAwareSecurityProtection } from "@/components/SecurityProtection";

// Public pages — kept as static imports
import Index from "./pages/Index";
import IAdvize from "./pages/IAdvize";
import Inscription from "./pages/Inscription";
import Privacy from "./pages/Privacy";
import Charter from "./pages/Charter";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import Challenge from "./pages/Challenge";


// Auth pages — lazy
const Auth = lazy(() => import("./pages/Auth"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));
const PasswordProtection = lazy(() => import("./pages/PasswordProtection"));

// Admin pages — lazy
const Admin = lazy(() => import("./pages/Admin"));
const AdminPostEditor = lazy(() => import("./pages/AdminPostEditor"));
const Admin2FA = lazy(() => import("./pages/Admin2FA"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminVisitors = lazy(() => import("./pages/AdminVisitors"));
const AdminResources = lazy(() => import("./pages/AdminResources"));
const AdminMembersInscriptions = lazy(() => import("./pages/AdminMembersInscriptions"));
const AdminAuditLogs = lazy(() => import("./pages/AdminAuditLogs"));
const AdminInscriptionLogs = lazy(() => import("./pages/AdminInscriptionLogs"));
const AdminLoginAttempts = lazy(() => import("./pages/AdminLoginAttempts"));
const AdminAnnouncements = lazy(() => import("./pages/AdminAnnouncements"));
const AdminSuggestions = lazy(() => import("./pages/AdminSuggestions"));
const AdminIPInsights = lazy(() => import("./pages/AdminIPInsights"));
const AdminIPDetail = lazy(() => import("./pages/AdminIPDetail"));
const AdminForcedChallenges = lazy(() => import("./pages/AdminForcedChallenges"));
const AdminCanaries = lazy(() => import("./pages/AdminCanaries"));

// Member pages — lazy
const EspaceMembre = lazy(() => import("./pages/EspaceMembre"));
const MemberResources = lazy(() => import("./pages/MemberResources"));
const MemberResourceCategory = lazy(() => import("./pages/MemberResourceCategory"));
const MemberResourceDetail = lazy(() => import("./pages/MemberResourceDetail"));
const MemberGettingStarted = lazy(() => import("./pages/MemberGettingStarted"));
const MemberScreenshotsGuide = lazy(() => import("./pages/MemberScreenshotsGuide"));
const MemberDashboard = lazy(() => import("./pages/MemberDashboard"));
const MemberSettings = lazy(() => import("./pages/MemberSettings"));
const MemberSuggestions = lazy(() => import("./pages/MemberSuggestions"));
const MemberSharedDrive = lazy(() => import("./pages/MemberSharedDrive"));
const MemberRoute = lazy(() => import("./components/MemberRoute"));

import { VisitorTracker } from "./components/VisitorTracker";
import { PublicThemeToggle } from "./components/PublicThemeToggle";
import MaintenancePage from "./components/MaintenancePage";

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isCheckingPasswordProtection, setIsCheckingPasswordProtection] = useState(true);
  const [passwordProtectionEnabled, setPasswordProtectionEnabled] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const refreshAdmin = async (userId: string | undefined | null) => {
      if (!userId) {
        setIsAdmin(false);
        return;
      }
      try {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('role', 'admin')
          .maybeSingle();
        setIsAdmin(!!roleData);
      } catch {
        setIsAdmin(false);
      }
    };

    const checkSettings = async () => {
      try {
        // Check password protection and maintenance mode
        const { data, error } = await supabase
          .from('site_settings')
          .select('setting_key, setting_value')
          .in('setting_key', ['password_protection', 'maintenance_mode']);

        if (!error && data) {
          data.forEach((setting) => {
            if (setting.setting_value && typeof setting.setting_value === 'object' && 'enabled' in setting.setting_value) {
              if (setting.setting_key === 'password_protection') {
                setPasswordProtectionEnabled(setting.setting_value.enabled as boolean ?? false);
              } else if (setting.setting_key === 'maintenance_mode') {
                setMaintenanceMode(setting.setting_value.enabled as boolean ?? false);
              }
            }
          });
        }

        const { data: { session } } = await supabase.auth.getSession();
        await refreshAdmin(session?.user?.id);
      } catch (err) {
        console.error('Error checking settings:', err);
      } finally {
        setIsCheckingPasswordProtection(false);
      }
    };

    checkSettings();

    // Keep admin status fresh on login/logout so the protection layer can be
    // disabled for authenticated admins without requiring a hard reload.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      refreshAdmin(session?.user?.id);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);


  useEffect(() => {
    const validateSession = async () => {
      // Si la protection par mot de passe est désactivée, bypasser la validation
      if (!passwordProtectionEnabled) {
        setIsAuthenticated(true);
        setIsChecking(false);
        return;
      }

      // Token is now in httpOnly cookie, no need to read from localStorage
      try {
        const { data, error } = await supabase.functions.invoke("validate-session", {
          body: {},
        });

        if (error) {
          setIsAuthenticated(false);
        } else if (data?.valid) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    validateSession();
  }, [passwordProtectionEnabled]);

  if (isCheckingPasswordProtection || isChecking) {
    return null;
  }

  // Show maintenance page if enabled and user is not admin
  if (maintenanceMode && !isAdmin) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SecurityProtection disabled={isAdmin} />
          <Toaster />
          <Sonner />
          <MaintenancePage />
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SecurityProtection disabled={isAdmin} />
          <Toaster />
          <Sonner />
          <Suspense fallback={<PageLoader />}>
            <PasswordProtection onAuthenticated={() => setIsAuthenticated(true)} />
          </Suspense>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteAwareSecurityProtection isAdmin={isAdmin} />
          <VisitorTracker />
          <PublicThemeToggle />
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/challenge" element={<Challenge />} />

                <Route path="/iadvize" element={<IAdvize />} />
                <Route path="/inscription" element={<Inscription />} />
                <Route path="/confidentialite" element={<Privacy />} />
                <Route path="/charte" element={<Charter />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/verify" element={<AuthVerify />} />
                <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                <Route path="/admin/articles" element={<AdminRoute><Admin /></AdminRoute>} />
                <Route path="/admin/2fa" element={<AdminRoute><Admin2FA /></AdminRoute>} />
                <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
                <Route path="/admin/posts/:id" element={<AdminRoute><AdminPostEditor /></AdminRoute>} />
                <Route path="/admin/visitors" element={<AdminRoute><AdminVisitors /></AdminRoute>} />
                <Route path="/admin/ip-insights" element={<AdminRoute><AdminIPInsights /></AdminRoute>} />
                <Route path="/admin/ip-insights/:ip" element={<AdminRoute><AdminIPDetail /></AdminRoute>} />
                <Route path="/admin/forced-challenges" element={<AdminRoute><AdminForcedChallenges /></AdminRoute>} />
                <Route path="/admin/canaries" element={<AdminRoute><AdminCanaries /></AdminRoute>} />
                <Route path="/admin/ressources" element={<AdminRoute><AdminResources /></AdminRoute>} />
                <Route path="/admin/membres" element={<AdminRoute><AdminMembersInscriptions /></AdminRoute>} />
                <Route path="/admin/audit" element={<AdminRoute><AdminAuditLogs /></AdminRoute>} />
                <Route path="/admin/logs-inscriptions" element={<AdminRoute><AdminInscriptionLogs /></AdminRoute>} />
                <Route path="/admin/login-attempts" element={<AdminRoute><AdminLoginAttempts /></AdminRoute>} />
                <Route path="/admin/annonces" element={<AdminRoute><AdminAnnouncements /></AdminRoute>} />
                <Route path="/admin/suggestions" element={<AdminRoute><AdminSuggestions /></AdminRoute>} />

                {/* Member Routes */}
                <Route path="/espace-membre" element={<EspaceMembre />} />
                <Route path="/espace-membre/dashboard" element={<MemberRoute><MemberDashboard /></MemberRoute>} />
                <Route path="/espace-membre/profil" element={<MemberRoute><MemberSettings /></MemberRoute>} />
                <Route path="/espace-membre/suggestions" element={<MemberRoute><MemberSuggestions /></MemberRoute>} />
                <Route path="/espace-membre/drive" element={<MemberRoute><MemberSharedDrive /></MemberRoute>} />
                <Route path="/espace-membre/guide-demarrage" element={<MemberRoute><MemberGettingStarted /></MemberRoute>} />
                <Route path="/espace-membre/ressources" element={<MemberRoute><MemberResources /></MemberRoute>} />
                <Route path="/espace-membre/ressources/captures-ecran" element={<MemberRoute><MemberScreenshotsGuide /></MemberRoute>} />
                <Route path="/espace-membre/ressources/:categorySlug" element={<MemberRoute><MemberResourceCategory /></MemberRoute>} />
                <Route path="/espace-membre/ressources/:categorySlug/:resourceSlug" element={<MemberRoute><MemberResourceDetail /></MemberRoute>} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
